// Animation utilities for Framer Motion
// These are reusable animation variants for different components

// Basic fade in animation
export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Fade in with y-axis movement (from bottom)
export const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: 50 
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.9]
    }
  }
};

// Fade in with y-axis movement (from top)
export const fadeInDown = {
  hidden: { 
    opacity: 0, 
    y: -50 
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.9]
    }
  }
};

// Fade in with x-axis movement (from left)
export const fadeInLeft = {
  hidden: { 
    opacity: 0, 
    x: -100 
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.9]
    }
  }
};

// Fade in with x-axis movement (from right)
export const fadeInRight = {
  hidden: { 
    opacity: 0, 
    x: 100 
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.9]
    }
  }
};

// Scale up animation
export const scaleUp = {
  hidden: { 
    opacity: 0, 
    scale: 0.8 
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.6, 0.05, 0.01, 0.9]
    }
  }
};

// Scale up with bounce
export const scaleUpBounce = {
  hidden: { 
    opacity: 0, 
    scale: 0.8 
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      type: "spring",
      stiffness: 400,
      damping: 10
    }
  }
};

// Dramatic entrance for 1st place
export const dramaticEntrance = {
  hidden: { 
    opacity: 0, 
    scale: 0.5,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      duration: 1.5,
      ease: [0.6, 0.05, 0.01, 0.9],
      scale: {
        type: "spring",
        stiffness: 300,
        damping: 10
      }
    }
  }
};

// Shimmer effect animation (for medals)
export const shimmer = {
  hidden: { 
    opacity: 0,
    background: "linear-gradient(to right, transparent, transparent)"
  },
  visible: { 
    opacity: 1,
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)",
    transition: { 
      duration: 1.5,
      repeat: Infinity,
      repeatType: "mirror"
    }
  }
};

// Pulse animation
export const pulse = {
  hidden: { 
    opacity: 0.8,
    scale: 1
  },
  visible: { 
    opacity: 1,
    scale: [1, 1.05, 1],
    transition: { 
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

// Glow effect animation
export const glow = {
  hidden: { 
    boxShadow: "0 0 0px rgba(0,212,255,0)"
  },
  visible: { 
    boxShadow: "0 0 15px rgba(0,212,255,0.7)",
    transition: { 
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

// Gold glow effect animation
export const goldGlow = {
  hidden: { 
    boxShadow: "0 0 0px rgba(255,215,0,0)"
  },
  visible: { 
    boxShadow: "0 0 20px rgba(255,215,0,0.7)",
    transition: { 
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

// Silver glow effect animation
export const silverGlow = {
  hidden: { 
    boxShadow: "0 0 0px rgba(192,192,192,0)"
  },
  visible: { 
    boxShadow: "0 0 15px rgba(192,192,192,0.7)",
    transition: { 
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

// Bronze glow effect animation
export const bronzeGlow = {
  hidden: { 
    boxShadow: "0 0 0px rgba(205,127,50,0)"
  },
  visible: { 
    boxShadow: "0 0 15px rgba(205,127,50,0.7)",
    transition: { 
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

// Screen shake animation
export const screenShake = {
  hidden: { 
    x: 0
  },
  visible: { 
    x: [0, -5, 5, -5, 5, 0],
    transition: { 
      duration: 0.5,
      ease: "easeInOut"
    }
  }
};

// Staggered children animation
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Animation for individual items in a list
export const listItem = {
  hidden: { 
    opacity: 0, 
    x: -20 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

// Animation for score counting up
export const countUp = {
  hidden: { 
    opacity: 0,
    scale: 0.8
  },
  visible: { 
    opacity: 1,
    scale: 1,
    transition: { 
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Animation timing constants
export const ANIMATION_TIMING = {
  INITIAL_DELAY: 1000, // 1s delay before starting
  FOURTH_PLACE_DELAY: 0, // Show 4th place immediately after initial delay
  THIRD_PLACE_DELAY: 2000, // 2s after 4th place
  SECOND_PLACE_DELAY: 2000, // 2s after 3rd place
  FIRST_PLACE_DELAY: 3000, // 3s after 2nd place (for suspense)
  CELEBRATION_DURATION: 3000 // 3s of celebration effects
};

// Animation sequence hook helper
export const createAnimationSequence = (callbacks) => {
  let totalDelay = ANIMATION_TIMING.INITIAL_DELAY;
  
  // Schedule 4th place reveal
  setTimeout(() => {
    callbacks.onFourthPlace();
  }, totalDelay + ANIMATION_TIMING.FOURTH_PLACE_DELAY);
  
  totalDelay += ANIMATION_TIMING.FOURTH_PLACE_DELAY + ANIMATION_TIMING.THIRD_PLACE_DELAY;
  
  // Schedule 3rd place reveal
  setTimeout(() => {
    callbacks.onThirdPlace();
  }, totalDelay);
  
  totalDelay += ANIMATION_TIMING.SECOND_PLACE_DELAY;
  
  // Schedule 2nd place reveal
  setTimeout(() => {
    callbacks.onSecondPlace();
  }, totalDelay);
  
  totalDelay += ANIMATION_TIMING.FIRST_PLACE_DELAY;
  
  // Schedule 1st place reveal
  setTimeout(() => {
    callbacks.onFirstPlace();
  }, totalDelay);
  
  totalDelay += ANIMATION_TIMING.CELEBRATION_DURATION;
  
  // Schedule end of celebration
  setTimeout(() => {
    if (callbacks.onComplete) {
      callbacks.onComplete();
    }
  }, totalDelay);
  
  // Return total animation duration
  return totalDelay;
};